<template>
    <div>
        <card>
            <v-card-title class="mb-6">General settings</v-card-title>
            <v-card-text>
                <v-radio-group
                    v-model="zone.restriction"
                    hide-details
                    @change="updateZoneRestriction"
                    class="pa-0 ma-0"
                    data-cy="zone_restrictions_radio_group"
                    :disabled="disabled"
                >
                    <v-radio
                        label="Anyone can reserve a desk in this zone"
                        data-cy="zone_restrictions_radio_unrestricted"
                        value="unrestricted"
                    ></v-radio>
                    <!--					Todo: check with chris why this one was diabled-->
                    <!--                    <v-radio  data-cy="zone_restrictions_radio_restricted_teams" label="Allow specified teams to reserve desks in this zone" value="restricted_teams"></v-radio>-->
                    <v-radio
                        data-cy="zone_restrictions_radio_restricted_teams_days"
                        label="Allow specified teams to reserve desks in this zone, but only on specific weekdays"
                        value="restricted_teams_days"
                    ></v-radio>
                </v-radio-group>
            </v-card-text>
            <!--            <v-divider class="my-8"></v-divider>-->
            <v-expand-transition>
                <div v-if="zone.restriction !== 'unrestricted'">
                    <v-divider class="my-8 mx-4"></v-divider>
                    <!--                        <v-card-title class="mb-6">Lift restrictions</v-card-title>-->
                    <v-card-text>
                        <alert>
                            This setting controls the time during the day from which it's possible for anyone,
                            <strong>in any team,</strong>
                            to make a reservation in this zone for the following day,
                            <strong>even</strong>
                            if they would normally be restricted from doing so.
                        </alert>
                        <v-select
                            filled
                            :items="liftRestrictionValues"
                            label="Lift restrictions for this zone at the following time"
                            data-cy="zone_restriction_lift_restriction_values"
                            item-text="label"
                            item-value="value"
                            v-model="zone.liftRestrictionsAt"
                            :disabled="disabled"
                        ></v-select>
                    </v-card-text>
                </div>
            </v-expand-transition>
        </card>

        <v-expand-transition>
            <card
                :title="`${useAllowWhitelist ? 'Allowed teams' : 'Restricted teams'}`"
                v-if="zone.restriction !== 'unrestricted'"
            >
                <v-card-text>
                    <div v-if="displayLegacyWhitelist && displayAllowWhitelist">
                        <v-btn
                            rounded
                            v-if="displayLegacyWhitelist"
                            color="primary"
                            text
                            large
                            :class="{ 'v-btn--active': !useAllowWhitelist }"
                            @click="useAllowWhitelist = false"
                        >
                            Legacy system
                        </v-btn>
                        <v-btn
                            rounded
                            v-if="displayAllowWhitelist"
                            color="primary"
                            text
                            large
                            :class="{ 'v-btn--active': useAllowWhitelist }"
                            @click="useAllowWhitelist = true"
                        >
                            New system
                        </v-btn>
                        <alert class="mt-6">
                            <p>
                                <strong>Legacy system:</strong>
                                Based on a blacklist. Any teams that are not added are allowed to make a reservation in
                                this zone at any time. Teams that are added are allowed to make reservations, but only
                                on the days for which the switch is turned on.
                            </p>
                            <p class="mb-0">
                                <strong>New system:</strong>
                                Based on a whitelist. Any teams that are not added are not allowed to make reservations
                                in this zone. Teams that are added are allowed to make reservations, but only on the
                                days for which the switch is turned on.
                            </p>
                        </alert>
                        <v-divider class="my-8"></v-divider>
                    </div>
                    <v-autocomplete
                        :disabled="disabled"
                        label="Add team"
                        class="mb-6"
                        rounded
                        data-cy="zone_restrictions_add_team"
                        filled
                        hide-details
                        :items="filteredTeams"
                        :item-value="(item) => item.id"
                        :item-text="(item) => `${item.name}`"
                        @input="(item) => addTeam(item.id)"
                        return-object
                        v-model="selectedTeam"
                    ></v-autocomplete>

                    <v-list v-if="restrictions.length">
                        <v-list-item
                            v-for="restriction of restrictions"
                            :key="`${restriction.team.id}/${restriction.zone.id}`"
                        >
                            <v-list-item-avatar>
                                <v-avatar class="text-h4 white--text" color="primary lighten-2">
                                    <!--                        <v-img :src="`https://source.unsplash.com/100x100/?face,${team.id}`"></v-img>-->
                                    <v-icon>mdi mdi-account-multiple</v-icon>
                                </v-avatar>
                            </v-list-item-avatar>
                            <v-list-item-content>
                                <v-list-item-title>
                                    <router-link :to="{ name: 'team', params: { teamId: restriction.team.id } }">
                                        {{ restriction.team.name }}
                                    </router-link>
                                </v-list-item-title>
                                <!--                                <v-list-item-subtitle>7 members</v-list-item-subtitle>-->
                            </v-list-item-content>
                            <v-list-item-icon>
                                <v-btn
                                    rounded
                                    @click.stop="removeRestriction(restriction.id)"
                                    xicon
                                    depressed
                                    class="text-h4"
                                    color="secondary"
                                    data-cy="zone_restrictions_remove_team"
                                    :data-team-id="restriction.team.id"
                                    :data-restriction-id="restriction.id"
                                    :disabled="disabled"
                                >
                                    <v-icon left>mdi mdi-delete</v-icon>
                                    Remove from zone
                                </v-btn>
                            </v-list-item-icon>
                        </v-list-item>
                    </v-list>
                    <div class="mt-12 text-center text--secondary" v-else>
                        No teams assigned to this zone - nobody will be able to book a desk in this zone.
                    </div>
                </v-card-text>
            </card>
        </v-expand-transition>

        <v-expand-transition>
            <card
                title="Weekdays"
                subtitle="When a switch is turned on, the corresponding team will be able to reserve a desk for that day"
                v-if="zone.restriction === 'restricted_teams_days'"
            >
                <v-card-text>
                    <v-simple-table data-cy="weekdays-restriction-table">
                        <thead>
                            <tr>
                                <th class="text-left">Team</th>
                                <th class="text-left">Monday</th>
                                <th class="text-left">Tuesday</th>
                                <th class="text-left">Wednesday</th>
                                <th class="text-left">Thursday</th>
                                <th class="text-left">Friday</th>
                                <th class="text-left grey--text">Saturday</th>
                                <th class="text-left grey--text">Sunday</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                data-cy="zone_restrictions_team_weekdays"
                                v-for="restriction of restrictions"
                                :key="`${restriction.team.id}/${restriction.zone.id}`"
                                :data-team-id="restriction.team.id"
                                :data-restriction-id="restriction.id"
                            >
                                <td>{{ restriction.team.name }}</td>
                                <td v-for="day of weekDays" :key="day">
                                    <v-switch
                                        :data-cy="`zone_restrictions_team_weekdays_switch_${day}`"
                                        @change="(v) => updateDay(restriction.id, day, v)"
                                        class="ma-0"
                                        hide-details
                                        color="primary"
                                        inset
                                        v-model="restriction[day]"
                                        :false-value="!useAllowWhitelist"
                                        :true-value="useAllowWhitelist"
                                        :disabled="disabled"
                                    ></v-switch>
                                </td>
                            </tr>
                        </tbody>
                    </v-simple-table>
                </v-card-text>
            </card>
        </v-expand-transition>
    </div>
</template>

<script>
import { teamService } from "@/services/team";
import { zoneService } from "@/services/zone";
import { DateTime } from "luxon";
import Card from "@/components/layout/card";
import { zoneRestrictionAllowService } from "@/services/restriction-allow";
import Alert from "../misc/alert.vue";

export default {
    name: "zone-restrictions",
    components: { Card, Alert },
    props: {
        customerId: {
            type: Number,
            required: true,
        },
        zone: {
            type: Object,
            required: true,
        },
        disabled: {
            type: Boolean,
        },
    },
    data() {
        return {
            restrictions: [],
            availableTeams: [],
            selectedTeam: null,
            weekDays: ["mon", "tue", "wed", "thu", "fri", "sat", "sun"],
            liftRestrictionsAt: null,
            liftRestrictionValues: [{ label: "Do not lift restrictions", value: null }],
            useAllowWhitelist: false, // is the new tab active? (set in created())
            displayAllowWhitelist: false, // is the old tab available? (set in created())
            displayLegacyWhitelist: false, // is the new tab available? (set in created())
        };
    },
    watch: {
        async "zone.liftRestrictionsAt"(time) {
            await zoneService.patch(this.zone.id, {
                liftRestrictionsAt: time,
            });
        },
        useAllowWhitelist: {
            async handler() {
                await this.refreshRestrictions();
            },
        },
    },
    computed: {
        filteredTeams() {
            return this.availableTeams.filter((t) => !this.restrictions.some((r) => r.team.id === t.id));
        },
    },
    async created() {
        // Determine whether to show the legacy system, both, or only the new one
        if (this.$can("use", "ZoneRestrictionAllow")) {
            // We're using the new system - hide the old tab, show the new tab and activate it
            this.displayLegacyWhitelist = false;
            this.displayAllowWhitelist = true;
            this.useAllowWhitelist = true;
        } else {
            // Still using the old one, make sure the tab is visible
            this.displayLegacyWhitelist = true;
        }

        if (this.$can("read", "ZoneRestrictionAllow")) {
            // We at least have access to the new system, show the tab
            this.displayAllowWhitelist = true;
        }

        // Calculate and populate restriction time values
        const startOfDay = DateTime.local().startOf("day");
        for (let i = 0; i < 24; i++) {
            const now = startOfDay.plus({ hours: i });
            this.liftRestrictionValues.push({
                label: now.toLocaleString(DateTime.TIME_24_SIMPLE),
                value: now.toISOTime({
                    includeOffset: false,
                    suppressMilliseconds: true,
                }),
            });
        }

        // Load teams
        await this.refreshAvailableTeams();

        // Manually trigger this for now  as the watcher wouldn't have triggered. Need to refactor this component anyway
        if (!this.useAllowWhitelist) await this.refreshRestrictions();
    },
    destroyed() {},
    methods: {
        async updateZoneRestriction(value) {
            await zoneService.patch(this.zone.id, {
                restriction: value,
            });
        },
        async updateDay(id, dayString, value) {
            if (this.useAllowWhitelist) {
                await zoneRestrictionAllowService.patch(id, {
                    [dayString]: value,
                });
            } else {
                throw new Error("Blacklist is EOL!");
            }
        },
        async refreshAvailableTeams() {
            this.availableTeams = await teamService.find({
                params: { customer: this.$props.customerId },
            });
        },
        async removeRestriction(restrictionId) {
            if (this.useAllowWhitelist) {
                await zoneRestrictionAllowService.delete(restrictionId);
            } else {
                throw new Error("Blacklist is EOL!");
            }
            await this.refreshRestrictions();
        },
        async addTeam(teamId) {
            this.$nextTick(() => {
                // Clear the input
                this.selectedTeam = null;
            });
            if (this.useAllowWhitelist) {
                const restriction = zoneRestrictionAllowService.build({ zoneId: this.zone.id, teamId });
                await zoneRestrictionAllowService.create(restriction);
            } else {
                throw new Error("Blacklist is EOL!");
            }
            await this.refreshRestrictions();
        },

        async refreshRestrictions() {
            console.log(`Loading ${this.useAllowWhitelist ? "WHITELIST" : "BLACKLIST"} restrictions`);
            if (this.useAllowWhitelist) {
                this.restrictions = await zoneRestrictionAllowService.find({
                    params: { zone: this.zone.id },
                });
            } else {
                throw new Error("Blacklist is EOL!");
            }
        },
    },
};
</script>
